import { useCallback, useEffect, useState } from "react";
import type { RaRecord, ShowProps } from "react-admin";
import { EditButton, Show, TopToolbar, useNotify, useRecordContext, useRefresh } from "react-admin";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { EMPLOYEE_TYPE } from "@/constants";
import { AttendanceDayDrawer } from "@/containers/staff/staff-attendance/components/AttendanceDayDrawer";
import type {
  AttendanceDayCell,
  AttendanceEmployeeRow,
  AttendanceMonthResponse,
  DayDrilldown,
} from "@/containers/staff/staff-attendance/attendance-types";
import { StaffMonthlyAttendancePanel } from "@/containers/staff/shared/StaffMonthlyAttendancePanel";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";

import { StaffActiveStatusChip } from "./components/StaffActiveStatusChip";
import { StaffLeaveBalanceCard } from "./components/StaffLeaveBalanceCard";
import { StaffLeaveHistorySection } from "./components/StaffLeaveHistorySection";
import { employeeGenderLabel } from "./staff-employee-form";
import { attendanceGridPath, resolveStaffDashboardMonth } from "./staff-show-helpers";

type EmployeeRecord = RaRecord & {
  id: number;
  name?: string;
  phone?: string;
  type?: string | null;
  identity?: string | null;
  isActive?: boolean;
  joinedOn?: string | null;
  leftOn?: string | null;
  gender?: string | null;
};

const StaffShowActions = () => (
  <TopToolbar>
    <EditButton label="Edit profile" />
  </TopToolbar>
);

function StaffMemberDashboardBody() {
  const record = useRecordContext<EmployeeRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [searchParams, setSearchParams] = useSearchParams();

  const [month, setMonth] = useState(() => resolveStaffDashboardMonth(searchParams.get("month")));
  const [loading, setLoading] = useState(false);
  const [employeeRow, setEmployeeRow] = useState<AttendanceEmployeeRow | null>(null);
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [offWeekdays, setOffWeekdays] = useState<number[]>([0]);
  const [lateCheckInLocal, setLateCheckInLocal] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DayDrilldown | null>(null);

  useEffect(() => {
    const fromUrl = resolveStaffDashboardMonth(searchParams.get("month"));
    setMonth((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const syncMonthToUrl = useCallback(
    (nextMonth: string) => {
      setMonth(nextMonth);
      const next = new URLSearchParams(searchParams);
      next.set("month", nextMonth);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const fetchAttendance = useCallback(async () => {
    if (record?.id == null) return;
    setLoading(true);
    try {
      const { data: response } = await callApi({
        location: "employeesAttendance",
        data: { month, employeeId: record.id },
        method: "GET",
      });
      const body = response as AttendanceMonthResponse;
      const row = Array.isArray(body?.data) ? (body.data[0] ?? null) : null;
      setEmployeeRow(row);
      setTimezone(body?.timezone || "Asia/Kolkata");
      setOffWeekdays(Array.isArray(body?.offWeekdays) ? body.offWeekdays : [0]);
      setLateCheckInLocal(body?.lateCheckInLocal ?? null);
    } catch {
      notify("Failed to load attendance", { type: "error" });
      setEmployeeRow(null);
    } finally {
      setLoading(false);
    }
  }, [month, notify, record?.id]);

  useEffect(() => {
    void fetchAttendance();
  }, [fetchAttendance]);

  if (!record) return null;

  const departmentLabel = record.type ? (EMPLOYEE_TYPE[record.type] ?? record.type) : "—";
  const gridHref = attendanceGridPath(month, record.type ?? null);

  const handleDayClick = (day: number, cell: AttendanceDayCell) => {
    if (!employeeRow) {
      setDrilldown({
        employee: {
          employeeId: Number(record.id),
          name: record.name ?? "",
          department: record.type ?? null,
          days: {},
          summary: {
            workingDaysInMonth: 0,
            presentDays: 0,
            incompleteDays: 0,
            absentDays: 0,
            leaveDays: 0,
            totalHours: 0,
          },
        },
        day,
        cell,
      });
      return;
    }
    setDrilldown({ employee: employeeRow, day, cell });
  };

  return (
    <Box sx={{ maxWidth: 960, px: { xs: 1, sm: 2 }, pb: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="h5" component="h1" fontWeight={600}>
              {record.name ?? "Staff"}
            </Typography>
            <StaffActiveStatusChip isActive={record.isActive} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Id {record.id}
            {record.phone ? ` · ${record.phone}` : ""}
            {` · ${departmentLabel}`}
            {record.identity ? ` · ITS ${record.identity}` : ""}
            {record.joinedOn ? ` · Joined ${formatListDate(record.joinedOn, { empty: "—" })}` : ""}
            {record.gender ? ` · ${employeeGenderLabel(record.gender)}` : ""}
          </Typography>
        </Box>

        <StaffLeaveBalanceCard employeeId={Number(record.id)} mode="office" />

        <StaffMonthlyAttendancePanel
          month={month}
          onMonthChange={syncMonthToUrl}
          loading={loading}
          employeeRow={employeeRow}
          timezone={timezone}
          offWeekdays={offWeekdays}
          lateCheckInLocal={lateCheckInLocal}
          onRefresh={() => {
            refresh();
            void fetchAttendance();
          }}
          onDayClick={handleDayClick}
          toolbarActions={
            <Button component={RouterLink} to={gridHref} variant="outlined" size="small">
              View in department grid
            </Button>
          }
        />

        <StaffLeaveHistorySection employeeId={Number(record.id)} />
      </Stack>

      <AttendanceDayDrawer
        open={drilldown != null}
        drilldown={drilldown}
        month={month}
        timezone={timezone}
        onClose={() => setDrilldown(null)}
        showStaffDashboardLink={false}
      />
    </Box>
  );
}

const StaffMemberDashboard = (props: ShowProps) => (
  <Show {...props} actions={<StaffShowActions />}>
    <StaffMemberDashboardBody />
  </Show>
);

export default StaffMemberDashboard;
